import type { NormalizedMarketRow } from '@/types/market';
import type { ScoreBand } from '@/types/insights';
import { clamp, pctChange } from '@/utils/numbers';
import { scoringWeights } from '@/config/scoringWeights';
import { magnitudeBand } from './bands';

/**
 * coolingRisk: 0..100, higher = greater risk this geography is heading into
 * a cooling phase soon, based on this period's trajectory (DOM lengthening,
 * inventory building, price growth stalling, sales volume dropping) rather
 * than its current level. A market can be "hot" today (low absolute DOM)
 * and still carry high coolingRisk if every trend line just inflected.
 */
export function computeCoolingRisk(row: NormalizedMarketRow): ScoreBand {
  const w = scoringWeights.coolingRisk;

  const domTrend = pctChange(row.medianDaysOnMarket, row.medianDaysOnMarketPrevPeriod);
  const moiTrend = pctChange(row.monthsOfInventory, row.monthsOfInventoryPrevPeriod);
  const priceTrend = row.medianSalePriceYoyPct;
  const salesTrend = pctChange(row.closedSales, row.closedSalesPrevPeriod);

  const parts: number[] = [];
  if (domTrend !== undefined) parts.push(domTrend * w.domTrendWeight);
  if (moiTrend !== undefined) parts.push(moiTrend * w.moiTrendWeight);
  if (priceTrend !== undefined) parts.push(priceTrend * w.priceTrendWeight);
  if (salesTrend !== undefined) parts.push(salesTrend * w.salesTrendWeight);

  if (parts.length === 0) return magnitudeBand(undefined);

  const composite = parts.reduce((a, b) => a + b, 0);
  const scaled = clamp(w.center + composite * w.scaleToRange, 0, 100);
  return magnitudeBand(scaled);
}
